import { NavLink, Outlet, useParams } from 'react-router-dom';
import { ThemeProvider } from '../theme/ThemeProvider';
import { CargaPagina } from '../components/Spinner';
import { Callout } from '../components/Callout';
import { Eyebrow } from '../components/Eyebrow';
import { Button } from '../components/Button';
import { AdminEventoDataProvider, useAdminEventoData } from './AdminEventoDataProvider';
import { exportarInvitacionesCSV } from './exportarCSV';

const TABS = [
  { to: '', end: true, texto: 'Resumen' },
  { to: 'invitados', texto: 'Invitados' },
  { to: 'equipo', texto: 'Equipo' },
  { to: 'lotes', texto: 'Lotes' },
  { to: 'mesas', texto: 'Mesas' },
  { to: 'alta-puerta', texto: 'Alta puerta' },
  { to: 'codigos', texto: 'Códigos' },
  { to: 'config', texto: 'Configuración' },
];

export function EventoDetalleLayout() {
  const { eventoId } = useParams<{ eventoId: string }>();
  if (!eventoId) return <Callout tono="error">Falta el identificador del evento.</Callout>;

  return (
    <AdminEventoDataProvider eventoId={eventoId}>
      <EventoDetalleContenido />
    </AdminEventoDataProvider>
  );
}

function EventoDetalleContenido() {
  const { evento, cargando, invitaciones, mesas, usuarios } = useAdminEventoData();

  if (cargando) return <CargaPagina />;
  if (!evento) return <Callout tono="error">Ese evento no existe.</Callout>;

  return (
    <ThemeProvider tema={evento.tema}>
      <div className="sticky top-14 z-10 w-full max-w-full border-b border-borde bg-fondo/95 backdrop-blur">
        <div className="mx-auto max-w-4xl px-3.5 pt-3.5 sm:px-6 sm:pt-6">
          {/* Cabecera adaptable: apila título y botones en pantallas muy pequeñas */}
          <div className="flex flex-col gap-3 pb-3 sm:flex-row sm:items-start sm:justify-between sm:pb-5">
            <div className="min-w-0 flex-1">
              <Eyebrow>Evento</Eyebrow>
              <h1 className="mt-0.5 truncate text-lg font-semibold text-texto sm:text-xl">
                {evento.nombre}
              </h1>
              <p className="truncate text-xs text-texto/60 sm:text-sm">
                {evento.fechaTexto} · {evento.lugarTexto}
              </p>
            </div>

            {/* Botones adaptativos: ocupan el ancho completo en móviles */}
            <div className="flex w-full shrink-0 gap-2 sm:w-auto">
              <Button
                variante="secundario"
                onClick={() => exportarInvitacionesCSV(evento, invitaciones, mesas, usuarios)}
                className="flex-1 px-3 py-2 text-xs sm:flex-none sm:px-5 sm:py-2.5 sm:text-sm"
              >
                Exportar base
              </Button>
              <NavLink
                to="config"
                className={({ isActive }) =>
                  [
                    'flex-1 inline-flex items-center justify-center rounded-full px-3 py-2 text-xs font-medium transition active:scale-[0.98] sm:flex-none sm:px-5 sm:py-2.5 sm:text-sm',
                    isActive
                      ? 'bg-navy text-white hover:bg-navy/90'
                      : 'border border-borde bg-white text-navy hover:border-navy/40',
                  ].join(' ')
                }
              >
                Editar evento
              </NavLink>
            </div>
          </div>

          {/* Navegación por Pestañas deslizable sin scrollbar visible */}
          <nav className="flex w-full gap-1 overflow-x-auto pb-px [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {TABS.map((t) => (
              <NavLink
                key={t.texto}
                to={t.to}
                end={t.end}
                className={({ isActive }) =>
                  [
                    'shrink-0 whitespace-nowrap rounded-t-lg px-3 py-2 text-xs font-medium transition sm:px-3.5 sm:py-2.5 sm:text-sm',
                    isActive
                      ? 'border-b-2 border-navy text-navy font-semibold'
                      : 'text-texto/50 hover:text-texto',
                  ].join(' ')
                }
              >
                {t.texto}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      <div className="mx-auto max-w-4xl p-3.5 pt-4 sm:p-6 sm:pt-5">
        <Outlet />
      </div>
    </ThemeProvider>
  );
}
